import React, { useState, useEffect, useCallback } from 'react';
import TeamMemberEntry from '../components/TeamMemberEntry/TeamMemberEntry';
import Select from '../components/Select/Select';
import styles from './Insights.module.scss';
import api from '../services/api';

function Insights({ skills, skillGroups, levels, teamMembers }) {
  const [strongestSkills, setStrongestSkills] = useState([]);
  const [weakestSkills, setWeakestSkills] = useState([]);
  const [growthSkills, setGrowthSkills] = useState([]);
  const [expertsBySkill, setExpertsBySkill] = useState({});
  const [groupedExperts, setGroupedExperts] = useState([]);
  const [topSkillsMetric, setTopSkillsMetric] = useState('average');
  const [improvementAreasMetric, setImprovementAreasMetric] = useState('average');

  // Calculate metrics when data changes
  useEffect(() => {
    calculateMetrics();
  }, [skills, teamMembers, levels, topSkillsMetric, improvementAreasMetric]);

  // Calculate all metrics for the insights page
  const calculateMetrics = useCallback(() => {
    // Skip if we don't have the necessary data
    if (!skills.length || !teamMembers.length || !levels.length) {
      setStrongestSkills([]);
      setWeakestSkills([]);
      setGrowthSkills([]);
      setExpertsBySkill({});
      setGroupedExperts([]);
      return;
    }

    // Map levels to their numeric values for scoring
    // We'll use the level order (level 1 = 1 point, level 2 = 2 points, etc.)
    const levelScores = {};
    levels.forEach((level, index) => {
      levelScores[level.id] = index + 1; // Start from 1
    });

    // Calculate average/median score for each skill
    const skillScores = {};
    const skillMembers = {}; // Track team members for each skill
    const focusSkills = {}; // Track skills with focus status

    skills.forEach(skill => {
      const scores = [];
      const members = [];
      const focusMembers = [];

      teamMembers.forEach(member => {
        if (!Array.isArray(member.skills)) return;

        const memberSkill = member.skills.find(s => s.skill_id === skill.id);
        if (memberSkill && memberSkill.level_id) {
          const score = levelScores[memberSkill.level_id] || 0;
          scores.push(score);
          members.push({
            member,
            score,
            levelId: memberSkill.level_id
          });

          // Track focus status
          if (memberSkill.status === 'focus') {
            focusMembers.push(member);
          }
        }
      });

      if (scores.length > 0) {
        // Calculate average
        const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
        
        // Calculate median
        const sortedScores = [...scores].sort((a, b) => a - b);
        const middle = Math.floor(sortedScores.length / 2);
        const median = sortedScores.length % 2 === 0
          ? (sortedScores[middle - 1] + sortedScores[middle]) / 2
          : sortedScores[middle];
        
        skillScores[skill.id] = {
          skill,
          average: average,
          median: median,
          count: scores.length,
          topScore: topSkillsMetric === 'average' ? average : median,
          improvementScore: improvementAreasMetric === 'average' ? average : median
        };
        
        skillMembers[skill.id] = members;
        
        // If there are focus members, track this skill
        if (focusMembers.length > 0) {
          focusSkills[skill.id] = {
            skill,
            focusCount: focusMembers.length,
            focusMembers
          };
        }
      }
    });

    // Sort skills by score and get top/bottom 10
    const sortedTopSkills = Object.values(skillScores).sort((a, b) => b.topScore - a.topScore);
    setStrongestSkills(sortedTopSkills.slice(0, 10));
    
    const sortedImprovementSkills = Object.values(skillScores).sort((a, b) => a.improvementScore - b.improvementScore);
    setWeakestSkills(sortedImprovementSkills.slice(0, 10));
    
    // Sort growth skills by focus count
    const sortedGrowthSkills = Object.values(focusSkills).sort((a, b) => b.focusCount - a.focusCount);
    setGrowthSkills(sortedGrowthSkills.slice(0, 10));

    // Find experts for each skill (team members with highest level)
    const experts = {};
    Object.entries(skillMembers).forEach(([skillId, members]) => {
      // Group by level
      const byLevel = {};
      members.forEach(({ member, levelId }) => {
        if (!byLevel[levelId]) byLevel[levelId] = [];
        byLevel[levelId].push(member);
      });

      // Get the highest level that has members
      const levelIds = Object.keys(byLevel).map(Number).sort((a, b) => b - a);
      if (levelIds.length > 0) {
        const highestLevelId = levelIds[0];
        experts[skillId] = {
          skill: skills.find(s => s.id === Number(skillId)),
          experts: byLevel[highestLevelId]
        };
      }
    });
    setExpertsBySkill(experts);

    // Group experts by skill group
    const expertsByGroup = {};
    Object.values(experts).forEach(({ skill, experts }) => {
      const groupId = skill.skill_group_id;
      const group = skillGroups.find(g => g.id === groupId);
      
      if (!expertsByGroup[groupId]) {
        expertsByGroup[groupId] = {
          group: group || { id: 'ungrouped', title: 'Ungrouped', color: 'hsl(0, 0%, 90%)' },
          skills: []
        };
      }
      
      expertsByGroup[groupId].skills.push({
        skill,
        experts
      });
    });
    
    setGroupedExperts(Object.values(expertsByGroup));
  }, [skills, teamMembers, levels, skillGroups, topSkillsMetric, improvementAreasMetric]);

  // Options for the metric type select
  const metricOptions = [
    { value: 'average', label: 'Average' },
    { value: 'median', label: 'Median' }
  ];

  // Handle metric type change for top skills
  const handleTopSkillsMetricChange = (value) => {
    setTopSkillsMetric(value);
  };

  // Handle metric type change for improvement areas
  const handleImprovementAreasMetricChange = (value) => {
    setImprovementAreasMetric(value);
  };

  return (
    <div className={styles.insights__container}>
      <div className={styles.insights__grid}>
        {/* Top Skills Card */}
        <div className={styles.insights__card}>
          <div className={styles['insights__card-header']}>
            <h2 className={styles['insights__card-title']}>Top Skills</h2>
            <div className={styles['insights__metric-selector']}>
              <Select
                options={metricOptions}
                value={topSkillsMetric}
                onChange={handleTopSkillsMetricChange}
                id="top-skills-metric"
                showSearch={false}
              />
            </div>
          </div>
          
          <div className={styles['insights__card-content']}>
            {strongestSkills.map((item, index) => {
              const skillGroup = skillGroups.find(g => g.id === item.skill.skill_group_id);
              const backgroundColor = skillGroup?.color || 'hsl(210, 50%, 95%)';
              
              return (
                <div key={item.skill.id} className={styles['insights__skill-item']}>
                  <span className={styles['insights__skill-item__rank']} style={{ backgroundColor }}>{index + 1}</span>
                  <div className={styles['insights__skill-item__info']}>               
                    <span className={styles['insights__skill-item__title']}>{item.skill.title}</span>
                    {skillGroup && (
                      <span 
                        className={styles['insights__skill-item__group']}   
                      >
                        {skillGroup.title}
                      </span>
                    )}
                  </div>
                  <div className={styles['insights__skill-item__score']}>
                    {item.topScore.toFixed(1)}
                  </div>
                </div>
              );
            })}
            {strongestSkills.length === 0 && (
              <p className={styles['no-data-message']}>No skill data available</p>
            )}
          </div>
        </div>

        {/* Areas for Improvement Card */}
        <div className={styles.insights__card}>
          <div className={styles['insights__card-header']}>
            <h2 className={styles['insights__card-title']}>Areas for Improvement</h2>
            <div className={styles['insights__metric-selector']}>
              <Select
                options={metricOptions}
                value={improvementAreasMetric}
                onChange={handleImprovementAreasMetricChange}
                id="improvement-areas-metric"
                showSearch={false}
              />
            </div>
          </div>
          
          <div className={styles['insights__card-content']}>
            {weakestSkills.map((item, index) => {
              const skillGroup = skillGroups.find(g => g.id === item.skill.skill_group_id);
              const backgroundColor = skillGroup?.color || 'hsl(210, 50%, 95%)';
              
              return (
                <div key={item.skill.id} className={styles['insights__skill-item']}>
                  <span className={styles['insights__skill-item__rank']} style={{ backgroundColor }}>{index + 1}</span>
                  <div className={styles['insights__skill-item__info']}>              
                    <span className={styles['insights__skill-item__name']}>{item.skill.title}</span>
                    {skillGroup && (
                      <span 
                        className={styles['insights__skill-item__group']}
                      >
                        {skillGroup.title}
                      </span>
                    )}
                  </div>
                  <div className={styles['insights__skill-item__score']}>
                    {item.improvementScore.toFixed(1)}
                  </div>
                </div>
              );
            })}
            {weakestSkills.length === 0 && (
              <p className={styles['no-data-message']}>No skill data available</p>
            )}
          </div>
        </div>

        {/* Growth Potential Card */}
        <div className={styles.insights__card}>
          <div className={styles['insights__card-header']}>
            <h2 className={styles['insights__card-title']}>Growth Potential</h2>
          </div>
          
          <div className={styles['insights__card-content']}>
            {growthSkills.map((item, index) => {
              const skillGroup = skillGroups.find(g => g.id === item.skill.skill_group_id);
              const backgroundColor = skillGroup?.color || 'hsl(210, 50%, 95%)';
              
              return (
                <div key={item.skill.id} className={styles['insights__skill-item']}>
                  <span className={styles['insights__skill-item__rank']} style={{ backgroundColor }}>{index + 1}</span>
                  <div className={styles['insights__skill-item__info']}>
                    <span className={styles['insights__skill-item__title']}>{item.skill.title}</span>
                    {skillGroup && (
                      <span 
                        className={styles['insights__skill-item__group']}
                      >
                        {skillGroup.title}
                      </span>
                    )}
                  </div>
                  <div className={styles['insights__skill-item__score']}>
                    {item.focusCount}
                  </div>
                </div>
              );
            })}
            {growthSkills.length === 0 && (
              <p className={styles['no-data-message']}>No growth focus areas set</p>
            )}
          </div>
        </div>

        {/* Experts by Skill Group Section */}
        <div className={`${styles.insights__card} ${styles['insights__card--experts']}`}>
          <div className={styles['insights__card-header']}>
            <h2 className={styles['insights__card-title']}>Skill Experts</h2>
          </div>
          
          <div className={styles['insights__skill-list']}>
            {groupedExperts.length === 0 && (
              <p className={styles['no-data-message']}>No experts identified yet</p>
            )}
            
            {groupedExperts.map(groupData => (
              <div 
                key={groupData.group.id} 
                className={styles['expert-group']}
              >
                
                {groupData.skills.map(skillData => (
                  <div 
                    key={skillData.skill.id}
                    className={styles['expert-item']} style={{ backgroundColor: groupData.group?.color || 'hsl(0, 0%, 90%)' }}
                  >

                    <h4 className={styles['expert-item__title']}>
                      {groupData.group.title} ∙ {skillData.skill.title}
                    </h4>

                    
                    <div className={styles['expert-item__members']}>
                      {skillData.experts.map(expert => (
                        <TeamMemberEntry
                          key={expert.id}
                          member={expert}
                          readOnly={true}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Insights; 